/* -------------------------- Design imports start -------------------------- */
import { Card, Container, Alert, AlertTitle } from '@mui/material'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import React, { type ReactElement, useEffect, useState } from "react";
import { ErrorBoundary as Boundary } from 'react-error-boundary'

import Button from '../Button'
/* ------------------------- Functional imports end ------------------------- */
interface Props {
  children: React.ReactNode
}

interface ErrorState {
  error: Error | null
  componentStack: string | null | undefined
}

const chunkReloadKey = 'apcs_chunk_reload'
const isChunkLoadError = (error: Error | null): boolean => {
  if (!error?.message) return false
  const message = error.message
  return (
    message.includes('ChunkLoadError') ||
    message.includes('Loading chunk') ||
    message.includes('Failed to fetch dynamically imported module')
  )
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ErrorBoundary({ children }: Props): ReactElement {
  /* -------------------------- Non state data start -------------------------- */
  /* --------------------------- Non state data end --------------------------- */
  /* ---------------------------- Flag states start --------------------------- */
  /* ----------------------------- Flag states end ---------------------------- */
  /* ---------------------------- Data states start --------------------------- */
  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    componentStack: '',
  })
  const isChunkError = isChunkLoadError(errorState.error)
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  const handleError = (error: Error, componentStack: string | null | undefined): void => {
    setErrorState({ error, componentStack })
    console.error(error, componentStack)
  }
  /* ------------------------- Callback functions end ------------------------- */

  useEffect(() => {
    if (!isChunkError || typeof window === 'undefined') return
    try {
      const hasReloaded = window.sessionStorage.getItem(chunkReloadKey) === 'true'
      if (!hasReloaded) {
        window.sessionStorage.setItem(chunkReloadKey, 'true')
        window.location.reload()
      }
    } catch {
      window.location.reload()
    }
  }, [isChunkError])

  /* -------------------------------------------------------------------------- */
  /*                              Render component                              */
  /* -------------------------------------------------------------------------- */
  return (
    <Boundary
      fallbackRender={() => (
        <Container maxWidth="sm">
          <Card variant="outlined">
            <Alert severity="error">
              <AlertTitle>
                {isChunkError ? 'Update required' : 'Oops.. Something went wrong'}
              </AlertTitle>
              {isChunkError ? (
                <>
                  It looks like a new version was deployed while you were using the app.
                  Please reload the page.
                  <pre>{errorState.error && errorState.error.message}</pre>
                  <Button
                    size="small"
                    color="inherit"
                    variant="outlined"
                    onClick={() => window.location.reload()}
                  >
                    Reload
                  </Button>
                </>
              ) : (
                <>
                  There must have been an error here. Please try again later or contact us with the
                  following info:
                  <pre>{errorState.error && errorState.error.message}</pre>
                  <Button
                    size="small"
                    color="inherit"
                    variant="outlined"
                    onClick={() => alert('TODO: Implement')}
                  >
                    Fehler übermitteln
                  </Button>
                </>
              )}
            </Alert>
          </Card>
        </Container>
      )}
      onError={(error, { componentStack }) => handleError(error, componentStack)}
    >
      {children}
    </Boundary>
  )
}
